import React, { useState } from 'react';
import { HistoryIcon, RotateCcwIcon, SaveIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { useNotes } from '../../contexts/NotesContext';
import { blockText } from '../../utils/notes';
import type { Note, ShareRole } from '../../types/notes';
export function VersionHistory({
  note,
  role
}: {
  note: Note;
  role: ShareRole;
}) {
  const {
    saveVersion,
    restoreVersion
  } = useNotes();
  const [previewId, setPreviewId] = useState<string | null>(null);
  const canEdit = role === 'owner' || role === 'editor';
  const preview = note.versions.find(version => version.id === previewId) ?? null;
  return <section className="mx-auto max-w-[68ch] border-t border-line px-5 py-5 sm:px-8" data-id="element-3998" data-mp="dgb88xqvcgw1">
      <div className="flex flex-wrap items-center gap-2" data-id="element-3999" data-mp="1sws6enwk1uvr">
        <HistoryIcon className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-4000" data-mp="1szeeqpr9sef1" />
        <h2 className="text-[14px] font-semibold text-ink" data-id="element-4001" data-mp="1ouy8gndqv4za">History</h2>
        {canEdit && <Button size="sm" variant="secondary" className="ml-auto" icon={<SaveIcon className="h-3.5 w-3.5" data-id="element-4003" data-mp="lned7yc7or5p" />} onClick={() => saveVersion(note.id, 'Manual snapshot')} data-id="element-4002" data-mp="gokxdz9gco2d">
            Save a version now
          </Button>}
      </div>

      {note.versions.length === 0 ? <p className="mt-3 text-[13px] text-ink-muted" data-id="element-4004" data-mp="un19y6tzx8sf">
          No snapshots yet. Save one before a big rewrite so you can always come back to it.
        </p> : <ul className="mt-3 divide-y divide-line rounded-card border border-line" data-id="element-4005" data-mp="20ztw48o0mtx">
          {note.versions.map(version => <li key={version.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-id="element-4006" data-mp="bqjdxd846mqd">
              <div className="min-w-0 flex-1" data-id="element-4007" data-mp="bse16y6uyqxm">
                <p className="text-[13px] font-medium text-ink" data-id="element-4008" data-mp="3afi86np78ylz">{version.summary}</p>
                <p className="cz-num text-[12px] text-ink-muted" data-id="element-4009" data-mp="3feusb0oorrkv">
                  {version.savedOn} · {version.author}
                  {version.blocks ? ` · ${version.blocks.length} blocks` : ''}
                </p>
              </div>
              {version.blocks ? <>
                  <Button size="sm" variant="ghost" onClick={() => setPreviewId(version.id)} data-id="element-4010" data-mp="vdzl7s3nje2v">
                    Preview
                  </Button>
                  {canEdit && <Button size="sm" variant="secondary" icon={<RotateCcwIcon className="h-3.5 w-3.5" data-id="element-4012" data-mp="3ut4jf01k4318" />} onClick={() => restoreVersion(note.id, version.id)} data-id="element-4011" data-mp="3m0ecaoz2qvg">
                      Restore
                    </Button>}
                </> : <Badge tone="neutral" data-id="element-4013" data-mp="7q83g8zce8u7">Event only</Badge>}
            </li>)}
        </ul>}

      <p className="mt-2 text-[12px] leading-5 text-ink-muted" data-id="element-4014" data-mp="1j3ak141fyyed">
        Restoring writes a new version rather than rewinding history, so the copy you are replacing
        stays recoverable.
      </p>

      <Modal open={preview !== null} onClose={() => setPreviewId(null)} title={preview ? `Version from ${preview.savedOn}` : ''} description={preview?.summary} width="lg" footer={<>
            <Button variant="ghost" onClick={() => setPreviewId(null)} data-id="element-4016" data-mp="1mlhs8awh0ayt">
              Close
            </Button>
            {canEdit && preview && <Button variant="primary" onClick={() => {
        restoreVersion(note.id, preview.id);
        setPreviewId(null);
      }} data-id="element-4017" data-mp="2fa597ua6se6k">
                Restore this version
              </Button>}
          </>} data-id="element-4015" data-mp="1vm5xag3vz0zk">
        {preview?.title && <p className="mb-2 text-[15px] font-semibold text-ink" data-id="element-4018" data-mp="2t7ts4wkp1b8z">{preview.title}</p>}
        <div className="max-h-72 space-y-1.5 overflow-y-auto rounded-lg border border-line bg-canvas px-4 py-3" data-id="element-4019" data-mp="2y1pppxx1ow3m">
          {(preview?.blocks ?? []).map(block => <p key={block.id} className={block.kind === 'heading' ? 'pt-1.5 text-[14px] font-semibold text-ink' : 'text-[13px] leading-6 text-ink-muted'} data-id="element-4020" data-mp="15cgx5kuk49nr">
              {block.kind === 'separator' ? '——————' : block.kind === 'checklist' ? `${block.checked ? '☑' : '☐'} ${blockText(block)}` : blockText(block) || ' '}
            </p>)}
        </div>
      </Modal>
    </section>;
}